import { useCallback, useEffect, useMemo, useState } from 'react'
import { Link } from 'react-router-dom'
import { api, ApiError } from '@/api/client'
import type { CrewDetail, EmployeeAdminRow } from '@/api/types'

interface Chip {
  key: string
  name: string
  id: string
}

/**
 * Chip list that collapses when long.
 *
 * Crews average 6 but the largest measured job-day was 43, and the unassigned
 * list can be the whole 119-strong field roster. Rendering those in full turns
 * a scannable card into a wall, so anything past the limit collapses behind a
 * count that expands on demand.
 */
function ChipList({ items, limit = 10 }: { items: Chip[]; limit?: number }) {
  const [expanded, setExpanded] = useState(false)
  const overflow = items.length - limit
  const shown = expanded ? items : items.slice(0, limit)

  return (
    <div className="chip-list">
      {shown.map((c) => (
        <span className="chip" key={c.key}>
          {c.name}
          <span className="chip-id">{c.id}</span>
        </span>
      ))}
      {overflow > 0 && (
        <button
          className="chip"
          style={{ cursor: 'pointer', fontWeight: 600 }}
          onClick={() => setExpanded((v) => !v)}
          aria-expanded={expanded}
        >
          {expanded ? 'Show fewer' : `+${overflow} more`}
        </button>
      )}
    </div>
  )
}

/**
 * Crew builder.
 *
 * A crew is the set of employees currently assigned to a supervisor. Building
 * them matters for two reasons:
 *
 *   1. Entry speed. The field picker shows the supervisor's own crew rather
 *      than all 138 field staff, which removes the slowest step in recording
 *      a crew day.
 *   2. It is the structure approval routing will need when crews start
 *      entering their own time — Sage has no org structure to route on.
 *
 * Removing someone CLOSES their assignment rather than deleting it, so a
 * submission from last month still resolves against the crew as it stood then.
 */
export function CrewsPage() {
  const [crews, setCrews] = useState<CrewDetail[] | null>(null)
  const [everyone, setEveryone] = useState<EmployeeAdminRow[]>([])
  const [error, setError] = useState<string | null>(null)
  const [notice, setNotice] = useState<string | null>(null)
  const [openId, setOpenId] = useState<number | null>(null)
  const [selected, setSelected] = useState<Set<number>>(new Set())
  const [saving, setSaving] = useState(false)

  const load = useCallback(async () => {
    setError(null)
    try {
      const [c, e] = await Promise.all([api.adminCrews(), api.adminEmployees()])
      setCrews(c)
      setEveryone(e)
    } catch (err) {
      setError(err instanceof ApiError ? err.message : 'Could not load crews')
    }
  }, [])

  useEffect(() => {
    void load()
  }, [load])

  /**
   * Who can be on a crew.
   *
   * Field staff only — employee numbers are prefixed by workforce and F is
   * field (dept 21). A = admin/sales, W = warehouse, S = supply sales. Office
   * and warehouse staff do not work crew days, so they are excluded rather
   * than offered and rejected. The API enforces the same rule.
   */
  const assignable = useMemo(
    () =>
      everyone.filter(
        (e) =>
          e.employee_no.toUpperCase().startsWith('F') &&
          e.has_name &&
          e.has_portal_record &&
          e.portal_role === 'Technician',
      ),
    [everyone],
  )

  const assignedElsewhere = useMemo(() => {
    const map = new Map<number, string>()
    for (const c of crews ?? []) {
      for (const m of c.members) {
        if (c.supervisor_employee_id !== openId) {
          map.set(m.employee_id, c.supervisor_name)
        }
      }
    }
    return map
  }, [crews, openId])

  function openCrew(c: CrewDetail) {
    setOpenId(c.supervisor_employee_id)
    setSelected(new Set(c.members.map((m) => m.employee_id)))
    setNotice(null)
    setError(null)
  }

  function toggle(id: number) {
    const next = new Set(selected)
    if (next.has(id)) next.delete(id)
    else next.add(id)
    setSelected(next)
  }

  async function save() {
    if (openId === null) return
    setSaving(true)
    setError(null)
    try {
      const updated = await api.adminSetCrewMembers(openId, [...selected])
      setNotice(`Saved — ${updated.members.length} on ${updated.supervisor_name}'s crew.`)
      setOpenId(null)
      await load()
    } catch (err) {
      setError(err instanceof ApiError ? err.message : 'Could not save crew')
    } finally {
      setSaving(false)
    }
  }

  const noSupervisors = crews !== null && crews.length === 0

  /**
   * Progress across the whole roster.
   *
   * Building 19 crews out of 119 people, the question that actually matters is
   * "who is left?" — not "what is on this one crew". Without it you are
   * cross-referencing cards by eye to find the people you have missed.
   */
  const summary = useMemo(() => {
    const assigned = new Set<number>()
    for (const c of crews ?? []) {
      for (const m of c.members) assigned.add(m.employee_id)
    }
    const unassigned = assignable.filter((e) => !assigned.has(e.employee_id!))
    return {
      assigned: assigned.size,
      unassigned,
      crewsBuilt: (crews ?? []).filter((c) => c.members.length > 0).length,
      totalCrews: (crews ?? []).length,
    }
  }, [crews, assignable])

  return (
    <div className="stack">
      {error && <div className="alert alert-error">{error}</div>}
      {notice && <div className="alert alert-info">{notice}</div>}

      <div className="card">
        <h2>Crews</h2>
        <p className="muted">
          A crew is the people who work under a supervisor. Supervisors see
          their own crew when recording a day, instead of the whole company.
          Only <strong>field employees (F numbers)</strong> can be on a crew —
          office, sales and warehouse staff are not offered.
        </p>
        <p className="muted" style={{ fontSize: '0.8125rem' }}>
          The supervisor is not listed in their own crew here, but is included
          automatically when recording time — they work the day too.
        </p>
        {noSupervisors && (
          <div className="alert alert-warn">
            No supervisors yet. On the{' '}
            <Link to="/office/employees">Employees</Link> screen, set someone's
            role to <strong>Supervisor</strong> and they will appear here.
          </div>
        )}
        {assignable.length === 0 && !noSupervisors && (
          <div className="alert alert-warn">
            No crew members available to assign. Add names on the{' '}
            <Link to="/office/employees">Employees</Link> screen first —
            employees need a name and the <strong>Technician</strong> role.
          </div>
        )}

        {crews !== null && assignable.length > 0 && (
          <p>
            <strong>{summary.crewsBuilt}</strong> of {summary.totalCrews} crews
            built · <strong>{summary.assigned}</strong> of {assignable.length}{' '}
            field staff assigned ·{' '}
            <strong>{summary.unassigned.length}</strong> not yet on a crew
          </p>
        )}
      </div>

      {summary.unassigned.length > 0 && (
        <div className="card">
          <div className="crew-heading">
            <h2 style={{ margin: 0 }}>Not on any crew</h2>
            <span className="count-pill count-pill-empty">
              {summary.unassigned.length}
            </span>
          </div>
          <p className="muted">
            These field staff have not been assigned to a supervisor. They can
            still be picked in the field, but their supervisor will be choosing
            from the full roster rather than a short list.
          </p>
          <ChipList
            limit={12}
            items={summary.unassigned.map((e) => ({
              key: e.sage_employee_key,
              name: `${e.first_name} ${e.last_name}`,
              id: e.employee_no,
            }))}
          />
        </div>
      )}

      {crews === null ? (
        <div className="card">
          <p className="muted">Loading…</p>
        </div>
      ) : (
        crews.map((c) => {
          const isOpen = openId === c.supervisor_employee_id
          return (
            <div className="card" key={c.supervisor_employee_id}>
              <div className="crew-heading">
                <h2 className="title" style={{ margin: 0 }}>
                  {c.supervisor_name}{' '}
                  <span className="mono muted" style={{ fontWeight: 400, fontSize: '0.875rem' }}>
                    {c.supervisor_employee_no}
                  </span>
                </h2>
                <span
                  className={`count-pill ${c.members.length === 0 ? 'count-pill-empty' : ''}`}
                  title={`${c.members.length} on this crew`}
                >
                  {c.members.length}
                </span>
              </div>

              {!isOpen ? (
                <>
                  {c.members.length === 0 ? (
                    <p className="muted">No crew assigned yet.</p>
                  ) : (
                    // Note the count differs from the field by one: the
                    // supervisor is not assigned to their own crew here, but
                    // does appear when recording time, because they work the
                    // day as well.
                    <ChipList
                      items={c.members.map((m) => ({
                        key: String(m.employee_id),
                        name: m.display_name,
                        id: m.employee_no,
                      }))}
                    />
                  )}
                  <button className="btn btn-secondary" onClick={() => openCrew(c)}>
                    {c.members.length === 0 ? 'Build crew' : 'Edit crew'}
                  </button>
                </>
              ) : (
                <>
                  <p className="muted">
                    {selected.size} selected. Someone already on another crew is
                    moved here — a person belongs to one crew at a time.
                  </p>
                  {/* Fixed height with its own scroll: the assignable pool is
                      119 people, so the Save button must stay reachable
                      without scrolling past the whole roster. */}
                  <div style={{ maxHeight: 360, overflowY: 'auto', marginBottom: '0.75rem' }}>
                    {assignable.map((e) => {
                      const id = e.employee_id!
                      const on = selected.has(id)
                      const elsewhere = assignedElsewhere.get(id)
                      return (
                        <button
                          key={e.sage_employee_key}
                          className={`btn ${on ? '' : 'btn-secondary'} btn-block`}
                          style={{ justifyContent: 'flex-start', marginBottom: 6 }}
                          aria-pressed={on}
                          onClick={() => toggle(id)}
                        >
                          <span style={{ width: '1.5rem' }}>{on ? '✓' : ''}</span>
                          {e.first_name} {e.last_name}
                          <span className="mono muted" style={{ fontWeight: 400 }}>
                            {e.employee_no}
                          </span>
                          {/* A bare supervisor name reads as unexplained noise.
                              Say what it means: this person is currently on
                              someone else's crew and selecting them moves them. */}
                          {elsewhere && !on && (
                            <span
                              className="badge badge-warn"
                              style={{ marginLeft: 'auto', fontWeight: 500 }}
                              title={`Currently on ${elsewhere}'s crew — selecting will move them here`}
                            >
                              on {elsewhere}'s crew
                            </span>
                          )}
                        </button>
                      )
                    })}
                  </div>
                  <div style={{ display: 'flex', gap: '0.5rem' }}>
                    <button className="btn" onClick={() => void save()} disabled={saving}>
                      {saving ? 'Saving…' : 'Save crew'}
                    </button>
                    <button className="btn btn-secondary" onClick={() => setOpenId(null)}>
                      Cancel
                    </button>
                  </div>
                </>
              )}
            </div>
          )
        })
      )}
    </div>
  )
}
